import { useEffect, useState } from "react";

function secondsUntil(deadlineMs: number): number {
  return Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));
}

/**
 * Live seconds remaining until `deadlineMs` (clamped at 0), re-derived from the wall clock every
 * ~500ms so it never drifts and survives a suspended tab.
 *
 * ⚠️ `deadlineMs` IS A DEVICE-CLOCK INSTANT, NOT A SERVER ONE. It used to take the server's
 * `expiresAt` ISO string, which put a server timestamp on one side of the subtraction and this
 * phone's clock on the other: a device running fast rendered every invite as already spent —
 * «منقضی شد» in place of پیوستن/رد — which is half of the 2026-08-17 report (the other half being
 * that the prompt never opened at all). Callers anchor the server's REMAINING time against
 * `Date.now()` on arrival (`game/invite-clock.ts`) and pass the result here, so both ends of this
 * subtraction are the same clock.
 */
export function useCountdown(deadlineMs: number): number {
  const [left, setLeft] = useState(() => secondsUntil(deadlineMs));
  useEffect(() => {
    setLeft(secondsUntil(deadlineMs));
    const id = window.setInterval(() => {
      setLeft((prev) => {
        const next = secondsUntil(deadlineMs);
        if (next === 0) window.clearInterval(id);
        return next === prev ? prev : next;
      });
    }, 500);
    return () => window.clearInterval(id);
  }, [deadlineMs]);
  return left;
}
